import {
  getOfflineSyncDoneRecord,
  OfflineSyncDoneRecord,
} from './sync-job-store';
import {
  isActiveSyncPhase,
  isSuccessfulSyncPhase,
  SyncJob,
} from './types';

export type OfflineCacheStatus =
  | { kind: 'none'; reason?: 'no_campaign' | 'no_scope' }
  | { kind: 'in_progress'; startedAt: number }
  | { kind: 'paused'; updatedAt: number }
  | {
      kind: 'ready';
      syncedAt: number;
      plots: number;
      analyses: number;
      includeHeatmaps: boolean;
      withErrors: boolean;
      errorCount: number;
      legacyDate?: boolean;
    }
  | { kind: 'stale' };

export type ResolveOfflineCacheStatusParams = {
  campaignId: number | null | undefined;
  managerId?: number;
  wideRole: boolean;
  job?: SyncJob;
  isSettingUpCache: boolean;
};

function jobMatchesScope(
  job: SyncJob,
  campaignId: number,
  managerId: number | undefined,
  wideRole: boolean
): boolean {
  if (job.scope.campaignId !== campaignId) {
    return false;
  }
  if (wideRole) {
    return (
      managerId != null &&
      job.scope.managerId != null &&
      job.scope.managerId === managerId
    );
  }
  return managerId == null
    ? job.scope.managerId == null
    : job.scope.managerId === managerId;
}

function readyFromJob(job: SyncJob): Extract<OfflineCacheStatus, { kind: 'ready' }> {
  return {
    kind: 'ready',
    syncedAt: job.updatedAt,
    plots: job.totals.plots,
    analyses: job.totals.analyses,
    includeHeatmaps: job.options.includeHeatmaps,
    withErrors: job.status === 'completed_with_errors',
    errorCount: job.errors.length,
  };
}

function readyFromRecord(
  record: OfflineSyncDoneRecord
): Extract<OfflineCacheStatus, { kind: 'ready' }> {
  return {
    kind: 'ready',
    syncedAt: record.at,
    plots: record.plots,
    analyses: record.analyses,
    includeHeatmaps: record.includeHeatmaps,
    withErrors: false,
    errorCount: 0,
    legacyDate: record.at <= 0,
  };
}

export function resolveOfflineCacheStatus(
  params: ResolveOfflineCacheStatusParams
): OfflineCacheStatus {
  const { campaignId, managerId, wideRole, job, isSettingUpCache } = params;

  if (campaignId == null) {
    return { kind: 'none', reason: 'no_campaign' };
  }

  if (wideRole && managerId == null) {
    return { kind: 'none', reason: 'no_scope' };
  }

  if (
    isSettingUpCache ||
    (job &&
      jobMatchesScope(job, campaignId, managerId, wideRole) &&
      isActiveSyncPhase(job.status))
  ) {
    return {
      kind: 'in_progress',
      startedAt: job?.createdAt ?? Date.now(),
    };
  }

  if (
    job &&
    jobMatchesScope(job, campaignId, managerId, wideRole) &&
    job.status === 'paused'
  ) {
    return { kind: 'paused', updatedAt: job.updatedAt };
  }

  if (
    job &&
    jobMatchesScope(job, campaignId, managerId, wideRole) &&
    isSuccessfulSyncPhase(job.status)
  ) {
    return readyFromJob(job);
  }

  const record = getOfflineSyncDoneRecord(campaignId, managerId);
  if (record) {
    return readyFromRecord(record);
  }

  if (
    job &&
    isSuccessfulSyncPhase(job.status) &&
    !jobMatchesScope(job, campaignId, managerId, wideRole)
  ) {
    return { kind: 'stale' };
  }

  return { kind: 'none' };
}
